/**
 * Constants for the All-E eligibility check v2.
 * Sources: All-E Recommendation Master Sheet, D2C configurator, HubSpot deal properties.
 */

import type { CheckStatus } from "./types";

//
// Hybrid V2 capacity at A-7/W55 (kW)
//
export const HYBRID_V2_CAPACITY = {
  single: {
    silent: 3.15,
    normal: 5.1,
    avg_with_silent_nights: 4.1,
  },
  duo: {
    silent: 6.3,
    normal: 9.0,
    avg_with_silent_nights: 7.7,
  },
  backup_heater: 1.8,
} as const;

// Reference Hybrid Duo capacity at A-7/W55, including the 1.8 kW backup heater.
// The backup is folded INTO this single number (no separate "+ 1.8 kW" displayed
// downstream). Two paths, picked by the installer's silent-mode-at-night toggle:
//
//   silentAtNight = false (DEFAULT, sheet R10 path):
//     normal mode 24/7 -> 9.0 + 1.8 = 10.8 kW
//
//   silentAtNight = true  (sheet D19 path, Bob Maks + Bob van Iersel + Luke
//                          Otto methodology, 2025-07-29):
//     normal day + silent night -> AVERAGE(6.3, 9.0) + 1.8 = 9.45 kW
//
// Default no-silent reflects Quatt's installer-channel reality: most homes
// don't need silent at night (vrijstaand, low-density). Installer toggles ON
// for noise-constrained installs (apartments, dense terraces).
export function getReferenceCapacityKw(silentAtNight: boolean): number {
  const hpComponent = silentAtNight
    ? (HYBRID_V2_CAPACITY.duo.silent + HYBRID_V2_CAPACITY.duo.normal) / 2 // 7.65
    : HYBRID_V2_CAPACITY.duo.normal;                                       // 9.0
  return hpComponent + HYBRID_V2_CAPACITY.backup_heater;
}

/** Default Hybrid Duo capacity (no silent at night). 10.8 kW. Kept for legacy reads. */
export const REFERENCE_CAPACITY_KW = getReferenceCapacityKw(false);

//
// Calculation constants
//
export const FULL_LOAD_HOURS = 1650;
export const GAS_CALORIFIC_VALUE_KWH = 8.8;
export const DEFAULT_BOILER_EFFICIENCY = 0.9;

//
// HTML rules engine constants (alle-check.html lines ~905-1455)
//
/** HP capacity (kW) by water-supply temp x pump x mode. Direct lift from HTML. */
export const HP_CAPACITY_BY_WATER_TEMP = {
  35: { single: { silent: 4.0,  normal: 6.7  }, duo: { silent: 7.0, normal: 11.5 } },
  45: { single: { silent: 3.6,  normal: 5.5  }, duo: { silent: 6.7, normal: 10.0 } },
  55: { single: { silent: 3.15, normal: 5.1  }, duo: { silent: 6.3, normal: 9.0  } },
} as const;

/** Backup heater (kW) added on top of single/duo capacity. */
export const BACKUP_KW = 1.8;

/** Full-load hours/year by insulation bucket. */
export const FULL_LOAD_HOURS_BY_INSULATION = {
  Goed: 1650,
  Gemiddeld: 2000,
  Slecht: 2400,
} as const;

/** Gas LHV factor: 35.19 MJ/m3 / 3.6 = 9.775 kWh/m3 (pre boiler-efficiency). */
export const GAS_LHV_FACTOR = 35.19 / 3.6;
/** Boiler efficiency for gas->heat conversion (HTML uses 0.9 flat). */
export const BOILER_EFFICIENCY = 0.9;

/** Radiator W/m2 lookup by radiator type (Stelrad/EN442). */
export const RAD_W_M2: Record<string, number> = {
  "10": 270,
  "11": 460,
  "20": 536,
  "21": 663,
  "22": 918,
  "33": 1326,
};

//
// Thermostat check
//
export const MAX_TEMP_DELTA = 2;

//
// Indoor temperature estimation
//
// Sheet "New Customer V2" D21 uses 20°C baseline (max-indoor at 100% coverage).
// We sit slightly above (20.5°C) so a fully-covered house reads as "warm
// enough" rather than "barely 20", but still more conservative than the
// previous 21°C baseline. Half-step toward Bob's framing.
export const DESIGN_OUTDOOR_TEMP = -7;
export const BASELINE_INDOOR_TEMP = 20.5;

//
// Check 0: Hard exclusions
//
// Stadsverwarming is intentionally NOT a hard exclusion for All-Electric:
// district-heat customers can still install an All-E heat pump (no gas meter
// required). Only Luchtverwarming stays as a hard block.
export const EXCLUSION_HEATING_TYPES = ["Luchtverwarming"] as const;
export const HIGH_TEMP_ONLY_TYPES = ["Vloerconvectoren"] as const;
export const COMPATIBLE_HEATING_TYPES = ["Radiatoren", "Vloerverwarming"] as const;

//
// Check 1: Enhanced heat demand thresholds
//
/** D2C configurator: >2500 m3 = Hybrid Duo only, no All-E */
export const GAS_CAP_THRESHOLD_M3 = 2500;

/** D2C recommendation logic blocks All-E for bad labels */
export const LABEL_BLOCK_LIST = ["E", "F", "G"] as const;

//
// Check 4: Insulation assessment
//
export const BUILD_YEAR_RANGES = [
  { value: "voor_1970", label: "Voor 1970", minMeasures: 3 },
  { value: "1970_1990", label: "1970-1990", minMeasures: 2 },
  { value: "1990_2005", label: "1990-2005", minMeasures: 2 },
  { value: "na_2005", label: "Na 2005", minMeasures: 1 },
] as const;

export const INSULATION_MEASURES = [
  { value: "spouwmuur", label: "Spouwmuurisolatie" },
  { value: "dak", label: "Dakisolatie" },
  { value: "vloer", label: "Vloerisolatie" },
  { value: "hr_glas", label: "HR++ of triple glas" },
] as const;

//
// Check 5/6/7: Physical + electrical + meerwerk
//
export const OUTDOOR_UNIT_OPTIONS = [
  { value: "begane_grond", label: "Begane grond", meerwerkFactor: false },
  { value: "muur_4m", label: "Muur tot 4m hoogte", meerwerkFactor: true },
  { value: "muur_4_8m", label: "Muur 4-8m hoogte", meerwerkFactor: true },
  { value: "dak", label: "Dak of dakkapel", meerwerkFactor: true },
  { value: "niet_mogelijk", label: "Niet mogelijk", meerwerkFactor: false },
] as const;

export const PIPING_DISTANCE_OPTIONS = [
  { value: "0_10", label: "0-10 meter", meerwerkFactor: false },
  { value: "10_15", label: "10-15 meter", meerwerkFactor: true },
  { value: "15_20", label: "15-20 meter", meerwerkFactor: true },
  { value: "meer_dan_20", label: "Meer dan 20 meter", meerwerkFactor: true },
  { value: "weet_niet", label: "Weet ik niet", meerwerkFactor: false },
] as const;

export const WALL_MATERIAL_OPTIONS = [
  { value: "baksteen", label: "Baksteen of spouwmuur", meerwerkFactor: false },
  { value: "beton", label: "Beton", meerwerkFactor: true },
  { value: "anders", label: "Anders", meerwerkFactor: false },
] as const;

export const MEERWERK_TIERS = {
  XS: { maxFactors: 0, label: "Standaard", surcharge: "€0" },
  S: { maxFactors: 1, label: "Licht meerwerk", surcharge: "~€400" },
  M: { maxFactors: 2, label: "Meerwerk", surcharge: "~€750" },
  L: { maxFactors: 999, label: "Complex", surcharge: "Neem contact op met Quatt" },
} as const;

//
// Water temperature distribution matrix
//
export type WaterTempStatus = "geschikt" | "ongeschikt" | "extra_controle";

interface WaterTempRule {
  cv_comfort_55c: boolean;
  distribution: string;
  status: WaterTempStatus;
  detail: string;
}

export const WATER_TEMP_RULES: WaterTempRule[] = [
  { cv_comfort_55c: true, distribution: "Vloerverwarming", status: "geschikt", detail: "Vloerverwarming is een lage temperatuur systeem, geschikt voor All-Electric." },
  { cv_comfort_55c: false, distribution: "Vloerverwarming", status: "geschikt", detail: "Vloerverwarming is een lage temperatuur systeem, geschikt voor All-Electric." },
  { cv_comfort_55c: true, distribution: "Radiatoren", status: "geschikt", detail: "CV comfortabel op 55C met radiatoren, geschikt voor All-Electric." },
  { cv_comfort_55c: false, distribution: "Radiatoren", status: "extra_controle", detail: "Radiatoren zonder comfort op 55C: extra controle nodig. Neem contact op met het quality team." },
  { cv_comfort_55c: true, distribution: "Vloerverwarming + andere bronnen", status: "geschikt", detail: "Vloerverwarming met aanvullende bronnen, comfortabel op 55C. Geschikt voor All-Electric." },
  { cv_comfort_55c: false, distribution: "Vloerverwarming + andere bronnen", status: "extra_controle", detail: "Gemengd systeem zonder comfort op 55C: extra controle nodig voor de aanvullende bronnen." },
];

//
// Disclaimer templates
//
export const DISCLAIMERS = {
  constant_temp: "De woning moet 24 uur per dag op dezelfde temperatuur verwarmd worden. Zonebediening (kamerthermostaten per ruimte) is niet geschikt voor All-Electric.",
  partial_coverage: (coveragePct: number, maxTemp: number) =>
    `De Hybrid Duo kan naar schatting ${coveragePct}% van de warmtevraag dekken. Bij -7C buitentemperatuur is de geschatte maximale binnentemperatuur ${maxTemp}C. De klant moet akkoord gaan met dit comfortverlies.`,
  water_temp_warning: "Er is een extra controle nodig voor de watertoevoertemperatuur. Neem contact op met het quality team voordat je verdergaat.",
  thermostat_warning: "Het verschil tussen dag- en nachttemperatuur is meer dan 2C. Voor All-Electric moet de klant bereid zijn om 24 uur dezelfde temperatuur aan te houden.",
  gas_cap: "Het jaarlijks gasverbruik is hoger dan 2.500 m3. De warmtevraag is te hoog voor All-Electric. Een Quatt Hybrid is een passender configuratie.",
  label_block: (label: string) =>
    `Energielabel ${label} duidt op onvoldoende isolatie voor All-Electric. Aanvullende isolatiemaatregelen zijn nodig, of een Quatt Hybrid is een passender configuratie.`,
  insulation_advisory: "De isolatie van deze woning is mogelijk onvoldoende voor optimaal rendement met All-Electric. Aanvullende isolatiemaatregelen zijn nodig voor optimaal rendement.",
  insulation_critical: "Oudere woning met weinig isolatie. All-Electric is technisch risicovol. Aanvullende isolatieverbeteringen zijn nodig voor een veilige installatie.",
  renovation_block: "De woning is in renovatie of vereist een grote CV-verplaatsing. Quatt voert geen waterzijdig inregelen uit.",
  heatbattery_space: "Er is onvoldoende ruimte voor de HeatBattery in de CV-ruimte.",
  no_outdoor_placement: "Er is geen geschikte locatie voor de buitenunit.",
  piping_long: "De leidingafstand is meer dan 20 meter. Dit is mogelijk niet haalbaar. Raadpleeg Quatt.",
  beton_wall: "Betonnen muur vereist specialistisch boren (meerwerk ~€199).",
  no_electrical_group: "Geen vrije groep beschikbaar in de meterkast (meerwerk ~€299).",
  panel_replacement: "De groepenkast moet mogelijk vervangen worden. Dit is een complexe installatie. Neem contact op met Quatt.",
  meerwerk_l_tier: "De installatie heeft 3 of meer complicerende factoren. Een standaard installatie is niet mogelijk. Plan een installatiecheck met Quatt.",
  stadsverwarming: "Stadsverwarming is niet compatibel met een warmtepomp. Er is geen gasmeter aanwezig.",
  luchtverwarming: "Een luchtverwarmingssysteem is niet compatibel met een warmtepompintegratie.",
  convectoren_only: "Alleen vloerconvectoren is een hoge-temperatuursysteem dat niet geschikt is voor All-Electric.",
  apartment_no_vve: "Voor een appartement is VVE-toestemming vereist voor de installatie van een buitenunit.",
  apartment_no_space: "Er is geen buitenruimte (balkon/tuin) beschikbaar voor de plaatsing van de buitenunit.",
} as const;
